/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { restartableTask as task, timeout } from 'ember-concurrency';
import type ModalService from 'walden-fast/services/modal';
import type ImpersonateService from 'walden-fast/services/impersonate';
import { SUPPORT_EMAIL } from 'walden-fast/constants/utils';

export interface MainContainerSignature {
  Args: {
    showActivateButton?: boolean;
    hideHeader?: boolean;
  };
  Blocks: {
    content: [];
    header: [];
    tabs: [];
  };
}

export default class MainContainerComponent extends Component<MainContainerSignature> {
  activateServiceModalName = 'activate-service';

  @service('modal') declare modal: ModalService;
  @service('impersonate') declare impersonateService: ImpersonateService;

  @tracked private hideImpersonationBar = false;
  @tracked private fadeOutStarted = false;
  private lastImpersonationId?: string;

  get supportEmail() {
    return SUPPORT_EMAIL;
  }

  constructor(owner: unknown, args: MainContainerSignature['Args']) {
    super(owner, args);
    this.checkImpersonationChange();
  }

  get activeImpersonation() {
    return this.impersonateService.impersonationAccount;
  }

  get impersonationBarClass() {
    if (this.hideImpersonationBar) {
      return 'hidden';
    }
    if (this.fadeOutStarted) {
      return 'opacity-0 transition-opacity duration-1000';
    }
    return 'opacity-100 transition-opacity duration-1000';
  }

  @action
  checkImpersonationChange() {
    const impersonation = this.activeImpersonation;
    const currentId = impersonation?.id;

    // Check if impersonation changed
    if (currentId !== this.lastImpersonationId) {
      this.lastImpersonationId = currentId;

      if (impersonation) {
        void this.impersonationTimer.perform();
      } else {
        this.resetImpersonationState();
      }
    }
  }

  @action
  openActivateServiceModal() {
    this.modal.open(this.activateServiceModalName);
  }

  @action
  clearImpersonation() {
    this.impersonateService.stopImpersonation();
  }

  @action
  resetImpersonationTimer() {
    if (this.activeImpersonation) {
      void this.impersonationTimer.perform();
    }
  }

  impersonationTimer = task(async () => {
    // Reset states
    this.resetImpersonationState();

    // Start fade out after 9 seconds
    await timeout(9000);
    this.fadeOutStarted = true;

    // Hide completely after 1 more second (total 10 seconds)
    await timeout(1000);
    this.hideImpersonationBar = true;
  });

  private resetImpersonationState() {
    this.hideImpersonationBar = false;
    this.fadeOutStarted = false;
  }
}
